/* cliente.ts — el único sitio donde este frontend habla con la API.
 *
 * Tres responsabilidades, y ninguna más:
 *   1. `credentials: 'include'` en TODA petición. En producción el front vive en
 *      `app.<dominio>` y la API en `api.<dominio>`: sin esto la cookie de sesión no viaja y
 *      todo responde 401. En desarrollo el proxy de Vite lo hace irrelevante, pero se manda
 *      igual para que el camino sea el mismo en los dos entornos.
 *   2. Traducir el 401 y el 403 del middleware a algo que la interfaz pueda decidir, en vez de
 *      redirigir por su cuenta como hacía `session.js`. Quién navega es asunto del router.
 *   3. Dar un error con mensaje legible: el servidor contesta `{"error": "…"}` en casi todos
 *      los fallos, y perder ese texto convierte cada avería en «algo salió mal».
 */

/** Base de la API. Vacía en desarrollo (el proxy de Vite atiende el mismo origen); en
 *  producción, el origen de `api.<dominio>` vía `VITE_API_BASE`. */
export const BASE = (import.meta.env['VITE_API_BASE'] ?? '').replace(/\/$/, '')

/**
 * URL absoluta de algo que sirve la API, para usar en un `href` o un `src`.
 *
 * Todo lo que NO es este frontend —la administración, `/logout`, `/static/…`— vive en el origen de
 * la API. Una ruta relativa funciona en desarrollo, porque el proxy de Vite cubre esos prefijos, y
 * en producción apunta al host estático y da un 404. Es una rotura que solo aparece al desplegar,
 * así que la regla vive en una función y no en la memoria de quien escribe el enlace.
 */
export function urlApi(ruta: string): string {
  return BASE + (ruta.startsWith('/') ? ruta : '/' + ruta)
}

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    mensaje: string,
    readonly cuerpo?: unknown,
  ) {
    super(mensaje)
    this.name = 'ErrorApi'
  }

  /** La sesión venció o nunca hubo. La interfaz debe llevar al ingreso. */
  get sinSesion(): boolean {
    return this.estado === 401
  }

  /** La cuenta no tiene la capacidad, o hay una clave temporal sin cambiar. */
  get sinPermiso(): boolean {
    return this.estado === 403
  }
}

type Opciones = RequestInit & { cuerpo?: unknown }

async function pedir(ruta: string, opciones: Opciones = {}): Promise<Response> {
  const { cuerpo, headers, body, ...resto } = opciones
  const init: RequestInit = {
    ...resto,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      // Con `body` (FormData) el navegador pone el Content-Type con su frontera; ponerlo a mano
      // rompe el multipart. Solo se declara para el JSON que serializamos aquí.
      ...(cuerpo === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
  }
  if (cuerpo !== undefined) init.body = JSON.stringify(cuerpo)
  else if (body !== undefined) init.body = body

  let r: Response
  try {
    r = await fetch(BASE + ruta, init)
  } catch (e) {
    // Falla de red, CORS mal configurado o el servidor caído. El navegador no distingue entre
    // ellos por diseño, así que tampoco lo fingimos aquí.
    throw new ErrorApi(0, 'No pude comunicarme con el servidor. Revisa tu conexión.', e)
  }
  if (!r.ok) throw await errorDe(r)
  return r
}

async function errorDe(r: Response): Promise<ErrorApi> {
  let cuerpo: unknown
  let mensaje = ''
  try {
    cuerpo = await r.json()
    if (cuerpo && typeof cuerpo === 'object') {
      const c = cuerpo as Record<string, unknown>
      // Tres claves porque la API usa tres: `error` en la mayoría de endpoints, `mensaje` en los
      // que devuelven `{ok: false, mensaje}` (`/api/perfil-nit`, `graph.perfil_secop`) y `detail`
      // en los errores que arma FastAPI. Sin `mensaje`, escribir un NIT que no está en el grafo
      // devolvía «El servidor respondió 500» en vez de la frase que el servidor sí redactó.
      if (typeof c['error'] === 'string') mensaje = c['error']
      else if (typeof c['mensaje'] === 'string') mensaje = c['mensaje']
      else if (typeof c['detail'] === 'string') mensaje = c['detail']
    }
  } catch {
    /* respuesta sin JSON: nos quedamos con el estado */
  }
  return new ErrorApi(r.status, mensaje || `El servidor respondió ${r.status}.`, cuerpo)
}

export async function obtener<T>(ruta: string, señal?: AbortSignal): Promise<T> {
  const r = await pedir(ruta, { method: 'GET', signal: señal ?? null })
  return (await r.json()) as T
}

/**
 * Como `obtener`, pero un **204 Sin contenido** es `null` y no un error.
 *
 * Existe porque «todavía no hay» es una respuesta legítima y frecuente —un pliego que nadie ha
 * analizado— y no un fallo que haya que enseñarle a nadie. `obtener` haría `r.json()` sobre un
 * cuerpo vacío y reventaría con un error de sintaxis que no describe nada de lo que pasó.
 */
export async function obtenerOpcional<T>(ruta: string, señal?: AbortSignal): Promise<T | null> {
  const r = await pedir(ruta, { method: 'GET', signal: señal ?? null })
  if (r.status === 204) return null
  const txt = await r.text()
  return txt ? (JSON.parse(txt) as T) : null
}

export async function enviar<T>(
  ruta: string,
  cuerpo?: unknown,
  señal?: AbortSignal,
  // Las escrituras de `/api/admin/*` exigen el testigo anti-CSRF en la cabecera `x-csrf` (viene
  // en `GET /api/me`). Va como extra y no siempre: el resto de la API se protege con la
  // verificación de `Origin` y añadir la cabecera dispararía preflights que no compran nada.
  extras: { headers?: Record<string, string> } = {},
): Promise<T> {
  const r = await pedir(ruta, {
    method: 'POST',
    cuerpo,
    signal: señal ?? null,
    ...(extras.headers ? { headers: extras.headers } : {}),
  })
  return (await r.json()) as T
}

export async function reemplazar<T>(
  ruta: string,
  cuerpo: unknown,
  señal?: AbortSignal,
  // `keepalive` deja que la petición termine aunque el navegador descarte la página. Solo hace
  // falta al guardar mientras la pestaña se va; su límite de cuerpo es de 64 KB.
  extras: { keepalive?: boolean } = {},
): Promise<T> {
  const r = await pedir(ruta, {
    method: 'PUT',
    cuerpo,
    signal: señal ?? null,
    ...(extras.keepalive ? { keepalive: true } : {}),
  })
  return (await r.json()) as T
}

export async function borrar<T>(ruta: string, señal?: AbortSignal): Promise<T> {
  const r = await pedir(ruta, { method: 'DELETE', signal: señal ?? null })
  return (await r.json()) as T
}

/** Sube un archivo (el RUP en PDF). Sin `Content-Type`: lo pone el navegador con su frontera. */
export async function subir<T>(ruta: string, campos: FormData, señal?: AbortSignal): Promise<T> {
  const r = await pedir(ruta, { method: 'POST', body: campos, signal: señal ?? null })
  return (await r.json()) as T
}

/** Abre un flujo POST y devuelve la respuesta cruda, para que `sse.ts` la lea por trozos. */
export async function abrirFlujo(
  ruta: string,
  cuerpo: unknown,
  señal?: AbortSignal,
): Promise<Response> {
  return pedir(ruta, {
    method: 'POST',
    cuerpo,
    headers: { Accept: 'text/event-stream' },
    signal: señal ?? null,
  })
}
